import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faPlus,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { stockTransferApi } from 'api/endpoints/StockTransfer';
import { categoryApi } from 'api/endpoints/Category';
import { getTenantFromToken } from 'utils/routeUtils';

function CreateStockTransfer() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const tenant = getTenantFromToken();

  // Header states
  const [transferDate, setTransferDate] = useState(
    new Date().toISOString().substring(0, 10)
  );
  const [fromOrganizationId, setFromOrganizationId] = useState('');
  const [fromWarehouseId, setFromWarehouseId] = useState('');
  const [toOrganizationId, setToOrganizationId] = useState('');
  const [toWarehouseId, setToWarehouseId] = useState('');
  const [refNo, setRefNo] = useState('');
  const [stockTransferCode, setStockTransferCode] = useState('');
  const [status, setStatus] = useState('draft');

  // Lists for dropdowns
  const [organizations, setOrganizations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [categories, setCategories] = useState([]);

  // Detail line items state
  const [items, setItems] = useState([
    {
      tempId: Date.now(),
      productCategoryId: '',
      productId: '',
      optionId: '',
      pricePerUnit: '',
      quantity: '',
      stockValue: 0,
      uom: '',
      productList: [],
      optionsList: []
    }
  ]);

  // UI/State controls
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load organizations, warehouses, and categories
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [orgRes, whRes, catRes] = await Promise.all([
          stockTransferApi.getOrganizations(),
          stockTransferApi.getWarehouses(),
          categoryApi.getCategoryList()
        ]);

        setOrganizations(orgRes.data?.results || orgRes.data || []);
        setWarehouses(whRes.data?.results || whRes.data || []);
        setCategories(catRes.data?.results || []);
      } catch (error) {
        console.error('Error fetching metadata:', error);
        toast.error('Failed to load initial form dropdowns');
      }
    };

    fetchMetadata();
  }, []);

  // Load existing stock transfer entry for editing
  useEffect(() => {
    if (isEditMode) {
      const fetchStockTransferDetails = async () => {
        setLoading(true);
        try {
          const response = await stockTransferApi.getStockTransfer(id);
          const data = response.data?.results;
          if (data) {
            setTransferDate(data.transfer_date);
            setFromOrganizationId(data.from_organization_id || '');
            setFromWarehouseId(data.from_warehouse_id || '');
            setToOrganizationId(data.to_organization_id || '');
            setToWarehouseId(data.to_warehouse_id || '');
            setRefNo(data.ref_no || '');
            setStockTransferCode(data.stock_transfer_code || '');
            setStatus(data.status || 'draft');

            if (data.items && data.items.length > 0) {
              const mappedItems = await Promise.all(
                data.items.map(async (item, index) => {
                  let products = [];
                  let options = [];
                  let selectedProduct = null;

                  if (item.product_category_id) {
                    try {
                      const prodRes = await stockTransferApi.getProductReport({
                        cursor: 0,
                        limit: 100,
                        filters: {
                          search_text: '',
                          start_date: '',
                          end_date: ''
                        },
                        category_id: item.product_category_id
                      });
                      products =
                        prodRes.data?.results?.data ||
                        prodRes.data?.results ||
                        [];
                      selectedProduct = products.find(
                        p => p.id === item.product_id
                      );
                    } catch (err) {
                      console.error(
                        'Failed to load products for category',
                        item.product_category_id,
                        err
                      );
                    }
                  }

                  if (selectedProduct) {
                    let productOptions = selectedProduct.options;
                    if (typeof productOptions === 'string') {
                      try {
                        productOptions = JSON.parse(productOptions);
                      } catch (e) {
                        console.error('Failed to parse options string:', e);
                      }
                    }
                    options = productOptions
                      ? Object.keys(productOptions).map(key => {
                          const optVal = productOptions[key];
                          let optLabel = '';
                          if (optVal && typeof optVal === 'object') {
                            optLabel =
                              optVal.option_name ||
                              optVal.name ||
                              optVal.label ||
                              key;
                          } else {
                            optLabel = String(optVal || key);
                          }
                          return {
                            value: key,
                            label: optLabel
                          };
                        })
                      : [];
                  }

                  return {
                    tempId: Date.now() + index,
                    productCategoryId: item.product_category_id || '',
                    productId: item.product_id || '',
                    optionId: item.option_id || '',
                    pricePerUnit: Number(item.price_per_unit) || '',
                    quantity: Number(item.quantity) || '',
                    stockValue: Number(item.stock_value) || 0,
                    uom: selectedProduct
                      ? selectedProduct.uom_name || selectedProduct.unit || ''
                      : '',
                    productList: products,
                    optionsList: options
                  };
                })
              );
              setItems(mappedItems);
            }
          }
        } catch (error) {
          console.error('Error fetching stock transfer details:', error);
          toast.error('Failed to load stock transfer entry details');
        } finally {
          setLoading(false);
        }
      };
      fetchStockTransferDetails();
    }
  }, [id, isEditMode]);

  // Add new item row
  const handleAddItem = () => {
    setItems([
      ...items,
      {
        tempId: Date.now(),
        productCategoryId: '',
        productId: '',
        optionId: '',
        pricePerUnit: '',
        quantity: '',
        stockValue: 0,
        uom: '',
        productList: [],
        optionsList: []
      }
    ]);
  };

  // Remove item row
  const handleRemoveItem = index => {
    if (items.length === 1) {
      toast.warning('At least one line item is required');
      return;
    }
    setItems(items.filter((_, idx) => idx !== index));
  };

  // Cascading dropdowns - category change
  const handleCategoryChange = async (index, catId) => {
    const updatedItems = [...items];
    updatedItems[index].productCategoryId = catId;
    updatedItems[index].productId = '';
    updatedItems[index].optionId = '';
    updatedItems[index].pricePerUnit = '';
    updatedItems[index].stockValue = 0;
    updatedItems[index].uom = '';
    updatedItems[index].productList = [];
    updatedItems[index].optionsList = [];

    if (catId) {
      try {
        const prodRes = await stockTransferApi.getProductReport({
          cursor: 0,
          limit: 100,
          filters: { search_text: '', start_date: '', end_date: '' },
          category_id: catId
        });
        updatedItems[index].productList =
          prodRes.data?.results?.data || prodRes.data?.results || [];
      } catch (error) {
        console.error('Error loading products for category:', error);
        toast.error('Failed to load products for selected category');
      }
    }
    setItems(updatedItems);
  };

  // Cascading dropdowns - product change
  const handleProductChange = (index, prodId) => {
    const updatedItems = [...items];
    updatedItems[index].productId = prodId;
    updatedItems[index].optionId = '';
    updatedItems[index].pricePerUnit = '';
    updatedItems[index].uom = '';
    updatedItems[index].optionsList = [];

    const selectedProduct = updatedItems[index].productList.find(
      p => p.id === prodId
    );

    if (selectedProduct) {
      updatedItems[index].uom =
        selectedProduct.uom_name || selectedProduct.unit || '';

      let productOptions = selectedProduct.options;
      if (typeof productOptions === 'string') {
        try {
          productOptions = JSON.parse(productOptions);
        } catch (e) {
          console.error('Failed to parse options string:', e);
        }
      }

      if (productOptions && typeof productOptions === 'object') {
        const optsList = Object.keys(productOptions).map(key => {
          const optVal = productOptions[key];
          let optLabel = '';
          if (optVal && typeof optVal === 'object') {
            optLabel = optVal.option_name || optVal.name || optVal.label || key;
          } else {
            optLabel = String(optVal || key);
          }
          return { value: key, label: optLabel };
        });
        updatedItems[index].optionsList = optsList;
      }

      if (!productOptions || Object.keys(productOptions).length === 0) {
        updatedItems[index].pricePerUnit =
          Number(selectedProduct.base_price) || '';
      }
    }

    const price = Number(updatedItems[index].pricePerUnit) || 0;
    const qty = Number(updatedItems[index].quantity) || 0;
    updatedItems[index].stockValue = price * qty;
    setItems(updatedItems);
  };

  // Option change handler
  const handleOptionChange = (index, optId) => {
    const updatedItems = [...items];
    updatedItems[index].optionId = optId;

    const selectedProduct = updatedItems[index].productList.find(
      p => p.id === updatedItems[index].productId
    );

    if (selectedProduct && optId) {
      let prices = selectedProduct.option_prices;
      if (typeof prices === 'string') {
        try {
          prices = JSON.parse(prices);
        } catch (e) {
          console.error('Failed to parse option_prices string:', e);
        }
      }
      if (prices && typeof prices === 'object' && prices[optId] !== undefined) {
        updatedItems[index].pricePerUnit = Number(prices[optId]) || '';
      } else {
        updatedItems[index].pricePerUnit =
          Number(selectedProduct.base_price) || '';
      }
    } else if (selectedProduct) {
      updatedItems[index].pricePerUnit =
        Number(selectedProduct.base_price) || '';
    }

    const price = Number(updatedItems[index].pricePerUnit) || 0;
    const qty = Number(updatedItems[index].quantity) || 0;
    updatedItems[index].stockValue = price * qty;
    setItems(updatedItems);
  };

  // Value change for numeric fields
  const handleItemValueChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value === '' ? '' : Number(value);
    const price = Number(updatedItems[index].pricePerUnit) || 0;
    const qty = Number(updatedItems[index].quantity) || 0;
    updatedItems[index].stockValue = price * qty;
    setItems(updatedItems);
  };

  // Totals
  const subtotalItems = items.filter(
    item => item.productCategoryId && item.productId
  ).length;

  const totalQuantity = items.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0
  );

  const totalTransferValue = items.reduce(
    (sum, item) => sum + (Number(item.stockValue) || 0),
    0
  );

  const formatUGX = val => {
    const numericVal = Number(val) || 0;
    return `UGX ${Math.round(numericVal).toLocaleString()}`;
  };

  // Form submission
  const handleSubmit = async (e, submitStatus) => {
    if (e) e.preventDefault();

    if (!fromOrganizationId) {
      toast.error('Transfer From Organization is required');
      return;
    }
    if (!fromWarehouseId) {
      toast.error('Transfer From Warehouse is required');
      return;
    }

    const validatedItems = items.filter(
      item =>
        item.productCategoryId && item.productId && Number(item.quantity) > 0
    );

    if (validatedItems.length === 0) {
      toast.error(
        'At least one completed product item with quantity > 0 is required'
      );
      return;
    }

    setSubmitting(true);

    const payload = {
      transfer_date: transferDate,
      from_organization_id: fromOrganizationId,
      from_warehouse_id: fromWarehouseId,
      to_organization_id: toOrganizationId || null,
      to_warehouse_id: toWarehouseId || null,
      ref_no: refNo,
      status: submitStatus || status,
      items: validatedItems.map(item => ({
        product_category_id: item.productCategoryId,
        product_id: item.productId,
        option_id: item.optionId || null,
        price_per_unit: Number(item.pricePerUnit) || 0,
        quantity: Number(item.quantity) || 0,
        stock_value: item.stockValue
      }))
    };

    try {
      let response;
      if (isEditMode) {
        response = await stockTransferApi.updateStockTransfer(id, payload);
      } else {
        response = await stockTransferApi.createStockTransfer(payload);
      }

      if (
        response.data?.success ||
        response.status === 201 ||
        response.status === 200
      ) {
        toast.success(
          response.data?.results || 'Stock Transfer Entry saved successfully!'
        );
        navigate(`/${tenant}/stock-transfer`);
      } else {
        toast.error(
          response.data?.results || 'Failed to save Stock Transfer entry'
        );
      }
    } catch (error) {
      console.error('Error submitting Stock Transfer form:', error);
      const errMsg =
        error.response?.data?.detail || error.message || 'Error occurred';
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center gap-3">
          <Button
            variant="outline-secondary"
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{ width: '40px', height: '40px' }}
            onClick={() => navigate(`/${tenant}/stock-transfer`)}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
          <div>
            <h4 className="mb-1 text-dark fw-bold">
              {isEditMode
                ? 'Edit Stock Transfer Entry'
                : 'Create Stock Transfer Entry'}
            </h4>
            <div className="text-muted" style={{ fontSize: '13px' }}>
              Inventory &gt; Stock Transfer &gt;{' '}
              <span className="text-primary">
                {isEditMode ? 'Edit' : 'New'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <Card className="shadow-sm border-0 py-5 text-center">
          <Card.Body>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading stock transfer details...</p>
          </Card.Body>
        </Card>
      ) : (
        <Form onSubmit={e => handleSubmit(e, status)}>
          <Row className="g-4">
            {/* Left Column */}
            <Col lg={9}>
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: '12px' }}
              >
                <Card.Body className="p-4">
                  <h5 className="card-title fw-bold text-dark mb-4">
                    Transfer General Details
                  </h5>

                  <Row className="g-3 mb-4">
                    {isEditMode && (
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label
                            className="fw-bold text-secondary mb-2"
                            style={{ fontSize: '13px' }}
                          >
                            Transfer ID
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={stockTransferCode}
                            disabled
                            style={{
                              borderRadius: '8px',
                              padding: '10px 14px',
                              border: '1px solid #D8E0F0',
                              backgroundColor: '#F8FAFC'
                            }}
                          />
                        </Form.Group>
                      </Col>
                    )}

                    <Col md={isEditMode ? 3 : 4}>
                      <Form.Group>
                        <Form.Label
                          className="fw-bold text-secondary mb-2"
                          style={{ fontSize: '13px' }}
                        >
                          Transfer Date <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={transferDate}
                          onChange={e => setTransferDate(e.target.value)}
                          required
                          style={{
                            borderRadius: '8px',
                            padding: '10px 14px',
                            border: '1px solid #D8E0F0'
                          }}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={isEditMode ? 3 : 4}>
                      <Form.Group>
                        <Form.Label
                          className="fw-bold text-secondary mb-2"
                          style={{ fontSize: '13px' }}
                        >
                          Transfer From Entity / Organization{' '}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          value={fromOrganizationId}
                          onChange={e => setFromOrganizationId(e.target.value)}
                          required
                          style={{
                            borderRadius: '8px',
                            padding: '10px 14px',
                            border: '1px solid #D8E0F0'
                          }}
                        >
                          <option value="">Select Organization</option>
                          {organizations.map(org => (
                            <option
                              key={org.organization_id || org.value}
                              value={org.organization_id || org.value}
                            >
                              {org.organization_name || org.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={isEditMode ? 3 : 4}>
                      <Form.Group>
                        <Form.Label
                          className="fw-bold text-secondary mb-2"
                          style={{ fontSize: '13px' }}
                        >
                          Transfer From Warehouse{' '}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          value={fromWarehouseId}
                          onChange={e => setFromWarehouseId(e.target.value)}
                          required
                          style={{
                            borderRadius: '8px',
                            padding: '10px 14px',
                            border: '1px solid #D8E0F0'
                          }}
                        >
                          <option value="">Select Warehouse</option>
                          {warehouses.map(wh => (
                            <option
                              key={wh.id || wh.value}
                              value={wh.id || wh.value}
                            >
                              {wh.name || wh.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label
                          className="fw-bold text-secondary mb-2"
                          style={{ fontSize: '13px' }}
                        >
                          Transfer To Entity / Org / Supplier / Customer
                        </Form.Label>
                        <Form.Select
                          value={toOrganizationId}
                          onChange={e => setToOrganizationId(e.target.value)}
                          style={{
                            borderRadius: '8px',
                            padding: '10px 14px',
                            border: '1px solid #D8E0F0'
                          }}
                        >
                          <option value="">Select Recipient...</option>
                          {organizations.map(org => (
                            <option
                              key={org.organization_id || org.value}
                              value={org.organization_id || org.value}
                            >
                              {org.organization_name || org.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label
                          className="fw-bold text-secondary mb-2"
                          style={{ fontSize: '13px' }}
                        >
                          Transfer To Warehouse
                        </Form.Label>
                        <Form.Select
                          value={toWarehouseId}
                          onChange={e => setToWarehouseId(e.target.value)}
                          style={{
                            borderRadius: '8px',
                            padding: '10px 14px',
                            border: '1px solid #D8E0F0'
                          }}
                        >
                          <option value="">Select Destination...</option>
                          {warehouses.map(wh => (
                            <option
                              key={wh.id || wh.value}
                              value={wh.id || wh.value}
                            >
                              {wh.name || wh.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label
                          className="fw-bold text-secondary mb-2"
                          style={{ fontSize: '13px' }}
                        >
                          Ref Number (Delivery Note / Invoice)
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter reference invoice number"
                          value={refNo}
                          onChange={e => setRefNo(e.target.value)}
                          style={{
                            borderRadius: '8px',
                            padding: '10px 14px',
                            border: '1px solid #D8E0F0'
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Items Table */}
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: '12px' }}
              >
                <Card.Body className="p-4">
                  <h5 className="card-title fw-bold text-dark mb-4">
                    Transfer Items
                  </h5>
                  <div className="table-responsive">
                    <Table hover className="align-middle mt-2 mb-4">
                      <thead className="bg-light border-0">
                        <tr>
                          <th
                            style={{ fontSize: '12px', width: '22%' }}
                            className="text-secondary fw-bold py-3"
                          >
                            Category
                          </th>
                          <th
                            style={{ fontSize: '12px', width: '22%' }}
                            className="text-secondary fw-bold py-3"
                          >
                            Product
                          </th>
                          <th
                            style={{ fontSize: '12px', width: '18%' }}
                            className="text-secondary fw-bold py-3"
                          >
                            Option (Size/Variety)
                          </th>
                          <th
                            style={{ fontSize: '12px', width: '14%' }}
                            className="text-secondary fw-bold py-3 text-end"
                          >
                            Price / Unit
                          </th>
                          <th
                            style={{ fontSize: '12px', width: '10%' }}
                            className="text-secondary fw-bold py-3 text-center"
                          >
                            Qty
                          </th>
                          <th
                            style={{ fontSize: '12px', width: '14%' }}
                            className="text-secondary fw-bold py-3 text-end"
                          >
                            Transfer Value
                          </th>
                          <th
                            style={{ width: '6%' }}
                            className="py-3 text-center"
                          ></th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <tr key={item.tempId}>
                            <td>
                              <Form.Select
                                value={item.productCategoryId}
                                onChange={e =>
                                  handleCategoryChange(index, e.target.value)
                                }
                                style={{
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  padding: '8px'
                                }}
                                required
                              >
                                <option value="">Select Category</option>
                                {categories.map(cat => (
                                  <option key={cat.value} value={cat.value}>
                                    {cat.label}
                                  </option>
                                ))}
                              </Form.Select>
                            </td>
                            <td>
                              <Form.Select
                                value={item.productId}
                                onChange={e =>
                                  handleProductChange(index, e.target.value)
                                }
                                disabled={!item.productCategoryId}
                                style={{
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  padding: '8px'
                                }}
                                required
                              >
                                <option value="">Select Product</option>
                                {item.productList.map(prod => (
                                  <option key={prod.id} value={prod.id}>
                                    {prod['Product Name'] ||
                                      prod.product_name ||
                                      prod.name}
                                  </option>
                                ))}
                              </Form.Select>
                            </td>
                            <td>
                              <Form.Select
                                value={item.optionId}
                                onChange={e =>
                                  handleOptionChange(index, e.target.value)
                                }
                                disabled={
                                  !item.productId ||
                                  item.optionsList.length === 0
                                }
                                style={{
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  padding: '8px'
                                }}
                              >
                                <option value="">No Option Selected</option>
                                {item.optionsList.map(opt => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </Form.Select>
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                min="0"
                                placeholder="Price"
                                value={item.pricePerUnit}
                                onChange={e =>
                                  handleItemValueChange(
                                    index,
                                    'pricePerUnit',
                                    e.target.value
                                  )
                                }
                                disabled={!item.productId}
                                style={{
                                  borderRadius: '6px',
                                  textAlign: 'right',
                                  fontSize: '13px',
                                  padding: '8px'
                                }}
                              />
                              {item.productId && (
                                <div
                                  className="text-muted text-end mt-1"
                                  style={{ fontSize: '11px' }}
                                >
                                  per {item.uom || 'Unit'}
                                </div>
                              )}
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                min="0"
                                placeholder="Qty"
                                value={item.quantity}
                                onChange={e =>
                                  handleItemValueChange(
                                    index,
                                    'quantity',
                                    e.target.value
                                  )
                                }
                                disabled={!item.productId}
                                style={{
                                  borderRadius: '6px',
                                  textAlign: 'center',
                                  fontSize: '13px',
                                  padding: '8px'
                                }}
                                required
                              />
                            </td>
                            <td
                              className="text-end fw-semibold text-dark"
                              style={{ fontSize: '13px' }}
                            >
                              {formatUGX(item.stockValue)}
                            </td>
                            <td className="text-center">
                              <Button
                                variant="link"
                                className="text-danger p-0 border-0"
                                onClick={() => handleRemoveItem(index)}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <Button
                    variant="outline-primary"
                    className="rounded-pill d-flex align-items-center gap-2 mt-2"
                    onClick={handleAddItem}
                    style={{ fontSize: '13px', padding: '6px 16px' }}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add Item Row
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            {/* Right Column: Summary */}
            <Col lg={3}>
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: '12px', backgroundColor: '#F8FAFC' }}
              >
                <Card.Body className="p-4">
                  <h5 className="fw-bold text-dark mb-4">Transfer Summary</h5>
                  <div className="d-flex justify-content-between mb-3">
                    <span
                      className="text-secondary"
                      style={{ fontSize: '13px' }}
                    >
                      Line Items
                    </span>
                    <span className="fw-bold text-dark">{subtotalItems}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-3">
                    <span
                      className="text-secondary"
                      style={{ fontSize: '13px' }}
                    >
                      Total Quantity
                    </span>
                    <span className="fw-bold text-dark">{totalQuantity}</span>
                  </div>
                  <hr
                    className="my-3"
                    style={{ borderTop: '1px solid #E2E8F0' }}
                  />
                  <div className="mb-4">
                    <div
                      className="text-secondary mb-1"
                      style={{ fontSize: '13px' }}
                    >
                      Total Transfer Value
                    </div>
                    <h3 className="fw-bold text-primary mb-0">
                      {formatUGX(totalTransferValue)}
                    </h3>
                  </div>
                  <div className="d-grid gap-2">
                    <Button
                      variant="primary"
                      className="rounded-pill py-2 font-weight-bold"
                      type="button"
                      disabled={submitting}
                      onClick={e => handleSubmit(e, 'posted')}
                    >
                      {submitting
                        ? 'Submitting...'
                        : 'Confirm & Submit Transfer'}
                    </Button>
                    <Button
                      variant="light"
                      className="rounded-pill py-2"
                      disabled={submitting}
                      onClick={() => navigate(`/${tenant}/stock-transfer`)}
                    >
                      Cancel
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      )}
    </Container>
  );
}

export default CreateStockTransfer;
